/**
 * The thirteen seamless gutter colours, and when a customer is shown them.
 *
 * Gutters are rolled on site from a coil, so the colour is chosen rather than
 * ordered from a shelf — and a customer picking one off a paper swatch card is
 * guessing. These are photographs of each colour on a real house, which is the
 * only way anybody has ever decided this.
 *
 * The page appears **whenever the document has gutters on it** (Jonathon,
 * 2026-08-16): in the price of a package, or offered underneath as an upgrade.
 * Gutter guards on their own are not gutters — they go on whatever is already
 * there, in whatever colour it already is — so a document offering only guards
 * does not get it.
 *
 * Look-book, not a form. The colour is settled with the rep in the same
 * conversation as the shingle colour; nothing here is ticked, recorded or
 * priced, and the document is not asking a question it would then have to
 * remember the answer to.
 */

/** One colour, with the photographs of it. */
export interface GutterColor {
  /** Stable, and the anchor the jump links point at. */
  key: string;
  name: string;
  /** Photograph keys, resolved to files by whatever draws this. */
  photos: string[];
}

/**
 * In the order the owner put them in, which is roughly darkest to lightest with
 * copper leading — the one people ask for by name.
 */
export const GUTTER_COLORS: readonly GutterColor[] = [
  { key: "copper", name: "Copper", photos: photosFor("copper", 5) },
  { key: "black", name: "Black", photos: photosFor("black", 7) },
  { key: "bronze", name: "Bronze", photos: photosFor("bronze", 5) },
  {
    key: "musket-brown",
    name: "Musket Brown",
    photos: photosFor("musket-brown", 2),
  },
  { key: "clay", name: "Clay", photos: photosFor("clay", 5) },
  { key: "wicker", name: "Wicker", photos: photosFor("wicker", 4) },
  { key: "almond", name: "Almond", photos: photosFor("almond", 5) },
  { key: "cream", name: "Cream", photos: photosFor("cream", 2) },
  { key: "eggshell", name: "Eggshell", photos: photosFor("eggshell", 5) },
  { key: "white", name: "White", photos: photosFor("white", 6) },
  { key: "dark-gray", name: "Dark Gray", photos: photosFor("dark-gray", 5) },
  {
    key: "tuxedo-gray",
    name: "Tuxedo Gray",
    photos: photosFor("tuxedo-gray", 2),
  },
  { key: "red", name: "Red", photos: photosFor("red", 4) },
];

/** `copper-1` … `copper-5`. The files are named the same way. */
function photosFor(key: string, count: number): string[] {
  return Array.from({ length: count }, (_, index) => `${key}-${index + 1}`);
}

/**
 * Whether a product on the document is gutters.
 *
 * Matched on the name, because that is what a document carries — a presented
 * document holds what the customer was told they were getting, not product
 * ids. "Premium 6" Seamless Gutters w/3"x4" Downspouts" is gutters; "Premium
 * Micromesh Stainless Steel Gutter Guards" is not, and the word "guard" is what
 * separates them.
 */
export function isGutterProduct(name: string): boolean {
  return /gutter/i.test(name) && !/guard/i.test(name);
}

/** Whether anything on this document puts gutters on the house. */
export function gutterColorsApply(names: readonly string[]): boolean {
  return names.some(isGutterProduct);
}
